import { useCallback, useEffect, useMemo, useState } from "react";

import isEmpty from "lodash/isEmpty";

import { GroupClassSeriesAnalyticsSources } from "@/constants/analytics/Analytics";
import { GroupClassCarouselVariantValues } from "@/constants/Schedule";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import usePatientStatus from "@/hooks/usePatientStatus";
import { useAnalytics } from "@/services/Analytics";
import { useGetFullProfileQuery } from "@/store/api/user";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import type { UserTabSelectorTabProps } from "@/ui/core/UserTabSelector";
import {
  getIsSelectedUserInTreatment,
  getTabSelectorOptions,
} from "@/utils/user.util";

const CarouselTitleMapper: Record<
  GroupClassCarouselVariant,
  (whoseGroups: string) => string
> = {
  basicGroups: () => "Groups",
  promotedGroups: () => "Explore groups",
  yourGroups: (whoseGroups: string) => `${whoseGroups} groups`,
};

interface SelectedCarouselUser {
  isInTreatment?: boolean;
  userId: string;
  userName?: string;
}

interface UseCarouselHeaderValuesProps {
  analyticsSource: GroupClassSeriesAnalyticsSources;
  variant: GroupClassCarouselVariant;
}

interface UseGroupClassCarouselHeader {
  onUserChange: (userId: string) => void;
  selectedCarouselUser: SelectedCarouselUser;
  title: string;
  userTabs: UserTabSelectorTabProps[];
}

const useGroupClassCarouselHeader = ({
  analyticsSource,
  variant,
}: UseCarouselHeaderValuesProps): UseGroupClassCarouselHeader => {
  const { trackUserSelectorEvent } = useAnalytics();
  const { isGroupClassRedesignEnabled } = useFeatureFlags();
  const {
    externalId: appUserId,
    isPatient: isAppUserPatient,
    isSupport: isAppUserSupport,
  } = useAppUserSelector();
  const { data: fullProfileData } = useGetFullProfileQuery();
  const { hasLinkedPatientInTreatment, isCurrentUserInTreatment } =
    usePatientStatus(fullProfileData);

  const [selectedUserId, setSelectedUserId] = useState<string>(appUserId);
  useEffect(() => {
    // since this component is rendered immediately on user login,
    // appUserId as returned by useAppUserSelector() has actually not been initialized yet
    // so we capture changes to appUserId and re-apply its value to selectedUserId here as needed
    if (!isEmpty(selectedUserId)) return;
    setSelectedUserId(appUserId);
  }, [selectedUserId, appUserId]);

  const proxyPatients = useAppUserProxyPatientsSelector();

  // Filter proxy patients based on variant
  // For "yourGroups" variant, show all proxy patients
  // For other variants, only show all patients in treatment
  const filteredProxyPatients = useMemo(() => {
    if (variant === GroupClassCarouselVariantValues.yourGroups) {
      return proxyPatients;
    }

    return proxyPatients?.filter((patient) =>
      getIsSelectedUserInTreatment({
        hasLinkedPatientInTreatment,
        isAppUserPatient,
        isCurrentUserInTreatment,
        selectedUserStatusCode: patient.universalStatusCode,
      }),
    );
  }, [
    variant,
    proxyPatients,
    hasLinkedPatientInTreatment,
    isAppUserPatient,
    isCurrentUserInTreatment,
  ]);

  // NOTE: this should NOT be limited to proxyPatientsInTreatment, but to ALL proxyPatients
  // as the "Your Groups" carousel variant is shown for ALL users, regardless of treatment status
  // however, the "Search Groups" modal is only for in-treatment patients, and thus filters further
  const userTabs = useMemo(
    () =>
      getTabSelectorOptions({
        appUserCount: 0,
        appUserId,
        isAppUserPatient,
        patients: filteredProxyPatients,
        shouldIncludeSelf: true,
      }) || [],
    [appUserId, isAppUserPatient, filteredProxyPatients],
  );

  const selectedCarouselUser: SelectedCarouselUser = useMemo(() => {
    if (selectedUserId === appUserId) {
      return {
        isInTreatment:
          isCurrentUserInTreatment ||
          (isAppUserSupport && hasLinkedPatientInTreatment),
        userId: appUserId,
        // we only show '<UserName>'s Groups' for support linkedPatients, not the app user
        userName: undefined,
      };
    }
    const { universalStatusCode, userName } =
      userTabs.find((item) => item.userId === selectedUserId) ?? {};
    const isInTreatment = getIsSelectedUserInTreatment({
      hasLinkedPatientInTreatment,
      isAppUserPatient,
      isCurrentUserInTreatment,
      selectedUserStatusCode: universalStatusCode,
    });
    return {
      isInTreatment,
      userId: selectedUserId,
      userName,
    };
  }, [
    appUserId,
    hasLinkedPatientInTreatment,
    isAppUserPatient,
    isAppUserSupport,
    isCurrentUserInTreatment,
    selectedUserId,
    userTabs,
  ]);

  const onUserChange = useCallback(
    (userId: string) => {
      setSelectedUserId(userId);
      trackUserSelectorEvent({
        component: GroupClassSeriesAnalyticsSources.GroupClassSeriesCarousel,
        selectedUser: userId,
        source: analyticsSource,
      });
    },
    [analyticsSource, trackUserSelectorEvent],
  );

  const { userName: forWhom } = selectedCarouselUser;
  const whoseGroups = forWhom ? `${forWhom}'s` : "Your";
  const title =
    isGroupClassRedesignEnabled &&
    variant === GroupClassCarouselVariantValues.promotedGroups
      ? "Recommended groups"
      : CarouselTitleMapper[variant](whoseGroups);

  return {
    onUserChange,
    selectedCarouselUser,
    title,
    userTabs,
  };
};

export type { UseGroupClassCarouselHeader };
export default useGroupClassCarouselHeader;
